import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import logo from '../assets/images/logo-artie.png';

export default function Onboarding3() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isConnecting, setIsConnecting] = useState(false);
  
  const isConnected = user?.social_profiles?.some(p => p.platform === 'youtube') || !!user?.youtube_data;

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      localStorage.setItem("oauth_return_path", "/onboarding/3");
      const { url } = await apiClient.connectYouTube();
      window.location.href = url;
    } catch (error) {
      console.error(error);
      toast.error("Failed to initiate YouTube connection");
      setIsConnecting(false);
    }
  };

  const handleContinue = () => {
    if (!isConnected) {
      toast.error("Please connect your YouTube channel to continue");
      return;
    }
    // Navigate to dashboard or next step
    navigate("/onboarding/4");
  };

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center">

      {/* Top Navigation */}
      <nav className="w-full max-w-5xl px-6 py-6 flex items-center justify-between z-50 relative">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-[oklch(1.0000_0_0)] border border-[oklch(0.92_0_0)] flex items-center justify-center text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:border-[oklch(0.2686_0_0)] transition-all"
          >
            <ArrowLeft className="w-5 h-5 stroke-[1.5]" />
          </button>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex gap-1">
            <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
            <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
            <div className="w-2 h-2 rounded-full bg-[oklch(0.70_0.18_55)]"></div>
            <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="text-xs font-medium text-[oklch(0.2686_0_0)] hover:text-[oklch(0.70_0.18_55)] transition-colors"
          >
            Skip
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 z-10 flex flex-col w-full max-w-2xl pt-8 pr-6 pb-32 pl-6 items-center">
        {/* Header - Artie Guidance */}
        <div className="w-full max-w-lg mb-10 animate-in slide-in-from-bottom-4 duration-500">
          <div className="flex items-start gap-5 bg-white p-6 rounded-2xl border border-[oklch(0.92_0_0)] shadow-sm">
            <div className="hidden sm:block shrink-0">
               <div className="w-16 h-16 rounded-full bg-[oklch(0.97_0.01_60)]/30 border-2 border-white shadow-md overflow-hidden">
                  <img src={logo} alt="Artie" className="w-full h-full object-cover" />
               </div>
            </div>
            <div className="space-y-1">
              <h1 className="text-2xl font-bold font-cabinet text-[oklch(0.2686_0_0)]">
                Let's calibrate your reach
              </h1>
              <p className="text-[oklch(0.55_0_0)] font-ibm text-sm leading-relaxed">
                 Connecting your YouTube channel helps me validate your audience data. This boosts the confidence of your Artie Score and makes your projects more attractive to sponsors.
              </p>
            </div>
          </div>
        </div>

        {/* Section 1: Youtube Connect Card */}
        <div className="w-full mb-8 animate-in slide-in-from-bottom-4 duration-500 delay-100">
          <div className="w-full bg-[oklch(1.0000_0_0)] border border-[oklch(0.92_0_0)] rounded-2xl p-8 md:p-12 flex flex-col items-center text-center shadow-sm relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-b from-[oklch(0.97_0.01_60)]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

            <div className="relative z-10 flex flex-col items-center w-full max-w-md">
              <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-md ring-1 ring-black/5">
                <svg viewBox="0 0 24 24" className="w-10 h-10 text-[#FF0000] fill-current" xmlns="http://www.w3.org/2000/svg">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"></path>
                </svg>
              </div>

              <h3 className="text-xl md:text-2xl font-semibold font-cabinet text-[oklch(0.2686_0_0)] mb-3 tracking-tight">
                YouTube Integration
              </h3>

              <p className="text-[oklch(0.55_0_0)] font-ibm text-sm md:text-base mb-8 leading-relaxed">
                Grant read-only access to your channel stats. This allows Artie to
                calibrate your scoring model based on real performance data.
              </p>

              <button 
                onClick={handleConnect}
                disabled={isConnecting || isConnected}
                className={`w-full sm:w-auto flex items-center justify-center gap-3 bg-[#FF0000] hover:bg-[#D90000] text-white px-8 py-3.5 rounded-xl font-medium transition-all shadow-lg shadow-red-500/10 hover:shadow-red-500/20 hover:-translate-y-0.5 active:translate-y-0 active:scale-95 disabled:opacity-70 disabled:hover:translate-y-0 disabled:cursor-not-allowed`}
              >
                {isConnected ? (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    <span>Connected</span>
                  </>
                ) : isConnecting ? (
                   <span>Connecting...</span>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"></path>
                    </svg>
                    <span>Connect YouTube Channel</span>
                  </>
                )}
              </button>

              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <div className="flex items-center gap-1.5 text-xs font-medium text-[oklch(0.55_0_0)]">
                  <Lock className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Encrypted & Private</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs font-medium text-[oklch(0.55_0_0)]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Official API</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer Action */}
      <footer className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[oklch(1.0000_0_0)] via-[oklch(1.0000_0_0)] to-transparent z-50 flex justify-center animate-in slide-in-from-bottom-4 duration-500 delay-400">
        <div className="w-full max-w-2xl flex items-center justify-end">
          <button
            onClick={handleContinue}
            disabled={!isConnected}
            className="group flex items-center gap-2 bg-[oklch(0.2686_0_0)] hover:bg-[oklch(0.2686_0_0)]/90 text-[oklch(1.0000_0_0)] px-8 py-3 rounded-xl text-sm font-medium transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5 font-ibm disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:translate-y-0"
          >
            <span>Continue</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </footer>
    </div>
  );
}